/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  Icons,
  Input,
  Menu,
  PLACEMENT,
  Popup,
  Text,
  THEME_CONSTANTS,
  themeHelpers,
} from '@web-stories-wp/design-system';

const NO_FOLDERS_VALUE = '__no-folders__';
const LOADING_VALUE = '__loading-folders__';

const FolderSelectRoot = styled.div`
  position: relative;
  width: 100%;
`;

const FolderInput = styled(Input)`
  width: 100%;
`;

const ChevronButton = styled.button(
  ({ $isOpen, theme }) => css`
    width: 32px;
    height: 32px;
    margin: -4px -10px -4px 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    color: ${theme.colors.fg.secondary};
    cursor: pointer;
    transform: ${$isOpen ? 'rotate(180deg)' : 'none'};

    ${themeHelpers.focusableOutlineCSS}

    svg {
      width: 32px;
      height: auto;
    }

    &:disabled {
      cursor: not-allowed;
      color: ${theme.colors.fg.disable};
    }
  `
);

const menuStylesOverride = css`
  margin-top: 8px;
  max-height: 188px;

  ul {
    margin: 0;
  }
`;

const FolderMenuItem = styled.li(
  ({ disabled, theme }) => css`
    position: relative;
    min-height: 32px;
    margin: 4px 8px;
    padding: 6px 8px;
    display: flex;
    align-items: center;
    border-radius: ${theme.borders.radius.small};
    cursor: ${disabled ? 'default' : 'pointer'};

    ${themeHelpers.focusableOutlineCSS(theme.colors.border.focus)};

    &:hover,
    &:focus {
      background-color: ${disabled ? 'transparent' : theme.colors.bg.tertiary};
    }

    ${disabled &&
    css`
      pointer-events: none;
    `}
  `
);

const FolderOptionText = styled(Text).attrs({
  forwardedAs: 'span',
  size: THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL,
})`
  min-width: 0;
  overflow: hidden;
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.fg.secondary : theme.colors.fg.primary};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const FolderOption = forwardRef(function FolderOption(
  { option, ...rest },
  ref
) {
  return (
    <FolderMenuItem {...rest} ref={ref} disabled={option.disabled}>
      <FolderOptionText disabled={option.disabled}>
        {option.label}
      </FolderOptionText>
    </FolderMenuItem>
  );
});

FolderOption.propTypes = {
  isSelected: PropTypes.bool,
  option: PropTypes.object.isRequired,
};

function TemplateFolderSelect({
  disabled,
  folders,
  inputId,
  isLoading,
  onChange,
  placeholder = 'Select folder...',
  value,
}) {
  const rootRef = useRef();
  const inputRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);

  const [filterValue, setFilterValue] = useState('');
  const selectedFolder = useMemo(
    () => folders.find((folder) => folder.id === value),
    [folders, value]
  );
  const selectedFolderLabel = selectedFolder?.path
    ?.map(({ name }) => name)
    .join(' / ');
  const trimmedValue = filterValue.trim();
  const lowerValue = trimmedValue.toLowerCase();

  const folderOptions = useMemo(
    () =>
      folders.filter(
        (folder) => folder && folder.id && Array.isArray(folder.path)
      ),
    [folders]
  );

  const visibleFolders = useMemo(() => {
    if (!isFiltering || !lowerValue) {
      return folderOptions;
    }

    return folderOptions.filter((folder) =>
      folder.path
        .map(({ name }) => name)
        .join(' / ')
        .toLowerCase()
        .includes(lowerValue)
    );
  }, [folderOptions, isFiltering, lowerValue]);
  const selectedFolderValue = selectedFolder?.id;
  const inputValue = isFiltering ? filterValue : selectedFolderLabel || '';

  const menuOptions = useMemo(() => {
    if (isLoading) {
      return [
        {
          group: [
            {
              disabled: true,
              label: 'Loading folders...',
              value: LOADING_VALUE,
            },
          ],
        },
      ];
    }

    const options = visibleFolders.map((folder) => ({
      label: folder.path.map(({ name }) => name).join(' / '),
      value: folder.id,
    }));

    if (options.length === 0) {
      options.push({
        disabled: true,
        label: 'No existing folders found',
        value: NO_FOLDERS_VALUE,
      });
    }

    return [{ group: options }];
  }, [isLoading, visibleFolders]);

  const handleInputFocus = useCallback(() => {
    setFilterValue('');
    setIsFiltering(false);
    setIsOpen(true);
  }, []);

  const handleChevronMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleToggle = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        inputRef.current?.focus();
        setFilterValue('');
      }

      setIsFiltering(false);
      setIsOpen(nextIsOpen);
    },
    [isOpen]
  );

  const handleInputChange = useCallback(
    (event) => {
      onChange('');
      setFilterValue(event.target.value);
      setIsFiltering(true);
      setIsOpen(true);
    },
    [onChange]
  );

  const handleMenuItemClick = useCallback(
    (_event, selectedValue) => {
      if (
        selectedValue === NO_FOLDERS_VALUE ||
        selectedValue === LOADING_VALUE
      ) {
        return;
      }

      onChange(selectedValue);
      setIsFiltering(false);
      setIsOpen(false);
      setFilterValue('');
    },
    [onChange]
  );

  const handleDismissMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <FolderSelectRoot ref={rootRef}>
      <FolderInput
        id={inputId}
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={isLoading ? 'Loading folders...' : placeholder}
        disabled={disabled}
        autoComplete="off"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        suffix={
          <ChevronButton
            type="button"
            $isOpen={isOpen}
            disabled={disabled}
            aria-label={isOpen ? 'Close folder list' : 'Open folder list'}
            onMouseDown={handleChevronMouseDown}
            onClick={handleToggle}
          >
            <Icons.ChevronDownSmall />
          </ChevronButton>
        }
      />
      {!disabled && isOpen && (
        <Popup
          anchor={rootRef}
          fillWidth
          isOpen={isOpen}
          placement={PLACEMENT.BOTTOM}
        >
          <Menu
            activeValue={selectedFolderValue}
            handleReturnToParent={() => inputRef.current?.focus()}
            isMenuFocused={false}
            listId={`${inputId}-folder-options`}
            menuAriaLabel="Folder options"
            menuStylesOverride={menuStylesOverride}
            onDismissMenu={handleDismissMenu}
            onMenuItemClick={handleMenuItemClick}
            options={menuOptions}
            parentId={inputId}
            renderItem={FolderOption}
          />
        </Popup>
      )}
    </FolderSelectRoot>
  );
}

TemplateFolderSelect.propTypes = {
  disabled: PropTypes.bool,
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      path: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  inputId: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.string,
};

export default TemplateFolderSelect;
